import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save } from "lucide-react";
import api from "../../lib/api";
import { Modal, Field, crmInput } from "../../components/crm";
import { toast } from "sonner";
import { PricingCatalog, RegionsEditor } from "./PricingCatalog";
import { useAuth } from "../../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";

const SCHEMAS = {
  services: { label: "Services", fields: [["title", "text"], ["description", "textarea"], ["icon", "text"], ["order", "number"]], title: (r) => r.title },
  portfolio: { label: "Portfolio", fields: [["title", "text"], ["client", "text"], ["category", "text"], ["description", "textarea"], ["thumbnail_url", "text"], ["video_url", "text"], ["featured", "bool"], ["order", "number"]], title: (r) => r.title },
  testimonials: { label: "Testimonials", fields: [["quote", "textarea"], ["name", "text"], ["company", "text"], ["position", "text"], ["avatar_url", "text"], ["order", "number"]], title: (r) => r.name },
  faqs: { label: "FAQs", fields: [["question", "text"], ["answer", "textarea"], ["order", "number"]], title: (r) => r.question },
  "client-logos": { label: "Client Logos", fields: [["name", "text"], ["image_url", "text"], ["order", "number"]], title: (r) => r.name },
  team: { label: "Team", fields: [["name", "text"], ["role", "text"], ["bio", "textarea"], ["avatar_url", "text"], ["order", "number"]], title: (r) => r.name },
};

function ResourceEditor({ resource }) {
  const schema = SCHEMAS[resource];
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const endpoint = resource === "portfolio" ? "portfolio" : resource;
  const contentPath = { services: "services", portfolio: "portfolio", testimonials: "testimonials", pricing: "pricing", faqs: "faqs", "client-logos": "client-logos", team: "team", addons: "addons", "delivery-options": "delivery-options", regions: "regions-all" }[resource];

  const load = () => api.get(`/content/${contentPath}`).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, [resource]);

  const openNew = () => { setEditing(null); const init = {}; schema.fields.forEach(([k, t]) => init[k] = t === "bool" ? false : t === "number" ? 0 : t === "list" ? "" : ""); setForm(init); setModal(true); };
  const openEdit = (r) => { setEditing(r); const f = { ...r }; if (Array.isArray(f.features)) f.features = f.features.join("\n"); setForm(f); setModal(true); };

  const save = async () => {
    const payload = { ...form };
    delete payload.id;
    schema.fields.forEach(([k, t]) => { if (t === "number") payload[k] = Number(payload[k]) || 0; if (t === "list") payload[k] = (payload[k] || "").split("\n").map((x) => x.trim()).filter(Boolean); });
    try {
      if (editing) await api.put(`/cms/${resource}/${editing.id}`, payload);
      else await api.post(`/cms/${resource}`, payload);
      toast.success("Saved"); setModal(false); load();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => { await api.delete(`/cms/${resource}/${id}`); load(); };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={openNew} className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangeDark text-white font-semibold px-4 py-2.5 rounded-lg" data-testid={`cms-add-${resource}`}><Plus size={17} /> Add {schema.label.replace(/s$/, "")}</button>
      </div>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.id} className="flex items-center gap-3 bg-white rounded-lg border border-border p-4" data-testid={`cms-item-${r.id}`}>
            {r.thumbnail_url || r.avatar_url || r.image_url ? <img src={r.thumbnail_url || r.avatar_url || r.image_url} alt="" className="w-10 h-10 rounded object-cover" /> : null}
            <div className="flex-1 min-w-0">
              <div className="font-medium text-brand-purpleDeep truncate">{schema.title(r)}</div>
              {r.featured && <span className="text-xs text-brand-orange">Featured</span>}
              {r.popular && <span className="text-xs text-brand-orange">Popular</span>}
            </div>
            <button onClick={() => openEdit(r)} className="text-brand-purpleDeep/40 hover:text-brand-purple"><Pencil size={16} /></button>
            <button onClick={() => remove(r.id)} className="text-brand-purpleDeep/40 hover:text-red-500"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={`${editing ? "Edit" : "Add"} ${schema.label.replace(/s$/, "")}`} wide>
        <div className="grid sm:grid-cols-2 gap-4">
          {schema.fields.map(([k, t]) => (
            <div key={k} className={t === "textarea" || t === "list" ? "sm:col-span-2" : ""}>
              <Field label={k.replace(/_/g, " ")}>
                {t === "textarea" ? (
                  <textarea rows={3} className={crmInput} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} />
                ) : t === "list" ? (
                  <textarea rows={5} className={crmInput} placeholder="One item per line" value={form[k] || ""} onChange={(e) => set(k, e.target.value)} />
                ) : t === "bool" ? (
                  <label className="flex items-center gap-2 text-sm text-brand-purpleDeep/70"><input type="checkbox" checked={!!form[k]} onChange={(e) => set(k, e.target.checked)} /> Enabled</label>
                ) : (
                  <input type={t === "number" ? "number" : "text"} className={crmInput} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} />
                )}
              </Field>
            </div>
          ))}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={() => setModal(false)} className="px-4 py-2.5 rounded-lg text-brand-purpleDeep/60 font-medium">Cancel</button>
          <button onClick={save} className="bg-brand-purple hover:bg-brand-purpleDark text-white font-semibold px-5 py-2.5 rounded-lg" data-testid="cms-save">Save</button>
        </div>
      </Modal>
    </div>
  );
}

function HomepageEditor() {
  const [s, setS] = useState(null);
  const set = (k, v) => setS((f) => ({ ...f, [k]: v }));
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)); }, []);
  if (!s) return null;
  const save = async () => { await api.put("/cms/settings", s); toast.success("Homepage updated"); };
  const FIELDS = [["hero_title", "Hero title"], ["hero_subtitle", "Hero subtitle"], ["trust_title", "Trust strip title"], ["stat_videos", "Stat: Videos"], ["stat_clients", "Stat: Clients"], ["stat_experience", "Stat: Experience"], ["featured_title", "Featured section title"], ["featured_video_url", "Featured video URL"], ["pricing_headline", "Pricing eyebrow"], ["pricing_subtitle", "Pricing subtitle"], ["final_cta_title", "Final CTA title"], ["final_cta_subtitle", "Final CTA subtitle"], ["contact_email", "Contact email"], ["contact_phone", "Contact phone"]];
  return (
    <div className="bg-white rounded-xl border border-border p-6 grid sm:grid-cols-2 gap-4">
      {FIELDS.map(([k, label]) => (
        <div key={k} className={k.includes("subtitle") ? "sm:col-span-2" : ""}>
          <Field label={label}>
            {k.includes("subtitle") ? <textarea rows={2} className={crmInput} value={s[k] || ""} onChange={(e) => set(k, e.target.value)} /> : <input className={crmInput} value={s[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`cms-${k}`} />}
          </Field>
        </div>
      ))}
      <div className="sm:col-span-2 flex justify-end">
        <button onClick={save} className="inline-flex items-center gap-2 bg-brand-purple hover:bg-brand-purpleDark text-white font-semibold px-5 py-2.5 rounded-lg" data-testid="cms-save-homepage"><Save size={16} /> Save homepage</button>
      </div>
    </div>
  );
}

export default function CMS() {
  const { user } = useAuth();
  const canPrice = user?.permissions_effective?.manage_pricing;
  if (!user?.permissions_effective?.manage_cms) return <div className="p-10 text-center text-brand-purpleDeep/60" data-testid="cms-no-access">You don't have access to the Website CMS.</div>;
  return (
    <div data-testid="admin-cms">
      <h1 className="font-heading font-extrabold text-3xl text-brand-purpleDeep">Website CMS</h1>
      <p className="mt-1 text-brand-purpleDeep/50">Edit everything on your public website — no developer needed.</p>
      <div className="mt-6">
        <Tabs defaultValue="homepage">
          <TabsList className="bg-white border border-border flex-wrap h-auto">
            <TabsTrigger value="homepage" data-testid="cms-tab-homepage">Homepage</TabsTrigger>
            {canPrice && <TabsTrigger value="catalog" data-testid="cms-tab-catalog">Pricing & Add-ons</TabsTrigger>}
            {Object.entries(SCHEMAS).map(([k, v]) => <TabsTrigger key={k} value={k} data-testid={`cms-tab-${k}`}>{v.label}</TabsTrigger>)}
            <TabsTrigger value="regions" data-testid="cms-tab-regions">Regions</TabsTrigger>
          </TabsList>
          <TabsContent value="homepage"><HomepageEditor /></TabsContent>
          {canPrice && <TabsContent value="catalog"><PricingCatalog /></TabsContent>}
          {Object.keys(SCHEMAS).map((k) => <TabsContent key={k} value={k}><ResourceEditor resource={k} /></TabsContent>)}
          <TabsContent value="regions"><RegionsEditor /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
